import { Component, ElementRef, Input, OnInit, OnDestroy, Inject } from '@angular/core';
import { LoggerService } from '@wdpr/ra-angular-logger';
import { switchMap, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import forEach from 'lodash-es/forEach';
import has from 'lodash-es/has';
import isEmpty from 'lodash-es/isEmpty';

// Services
import { ConfigService } from '@finder/core/config.service';
import { FinderMediaEngineService } from '@finder/shared/services/finder-media-engine/finder-media-engine.service';
import { FinderMediaEngineConfig } from '@finder/shared/services/finder-media-engine/finder-media-engine.interface';
import { LazyLoaderService } from '@finder/shared/services/lazy-loader/lazy-loader-service';
import { LoadingService } from '@finder/shared/services/loading-service/loading-service.service';
import { LocaleService } from '@finder/shared/services/locale-service/locale-service.service';
import { MealPeriodScheduleService } from '@finder/shared/services/meal-period-schedule/meal-period-schedule.service';
import { MyPlansService } from '@finder/shared/services/my-plans-service/my-plans.service';
import { ScheduleService } from '@finder/shared/services/schedule/schedule.service';
import { ViewportDetection } from '@finder/shared/services/viewport-detection/viewport-detection.service';
// Components
import { AvailabilityModalComponent } from '@finder/shared/components/availability-modal/availability-modal.component';
import { AccessibilityModalComponent } from '@finder/shared/components/a11y-modal/a11y-modal.component';
import { ModalComponent } from '@finder/shared/components/modal/modal.component';
import { ModalService } from '@finder/shared/components/modal/modal.service';
import { MyPlansModalComponent } from '@finder/shared/components/my-plans-modal/my-plans-modal.component';
import { VirtualQueueModalComponent } from '@finder/shared/components/virtual-queue-modal/virtual-queue-modal.component';
// Others
import { AAG_ANIMATIONS } from './aag.animations';
import { CONFIG_CONSTANTS, MEDIA_ENGINE_CONSTANTS, MODAL_DEEP_LINKS } from '@finder/shared/constants/app.constants';
import { GetFullDateStringProvider, GET_FULL_DATE_STRING } from '@finder/shared/utils/string/getFullDateString';
import { STATUS_CODES } from '@finder/shared/constants/response-code.constants';

@Component({
    selector: 'finder-aag',
    templateUrl: './aag.component.html',
    styleUrls: ['./aag.component.scss'],
    animations: [...AAG_ANIMATIONS]
})
export class AagComponent implements OnInit, OnDestroy {
    @Input() aagData: any;
    @Input() media: any;
    @Input() entityTypeUrl: string;
    @Input() entityId: string;
    @Input() native = false;
    @Input()
    set mediaEngineData(data: any) {
        this._mediaEngineData = data;
        this.initMediaEngine();
    }
    get mediaEngineData() {
        return this._mediaEngineData;
    }

    a11yModal: ModalComponent;
    actionButtonText: string;
    actionButtonUrl: string;
    addToMyPlans: any;
    addToMyPlansmModal: ModalComponent;
    atmpModalSubscriptions = [];
    availabilityModal: ModalComponent;
    breadcrumb: any;
    breadcrumbTemplate: string;
    calendarAction: string;
    calendarLabels: any;
    detailStateName: string;
    disclaimerText: string;
    discountsModal: ModalComponent;
    discountsModalLabels: any;
    gradientDisabledCSS: string;
    gradientEnabledCSS: string;
    gradientState: string | boolean;
    maxDays: number;
    mediaEngine: any = {};
    modalDeepLinks: any = {};
    showAag = false;
    showDiscountsModal = false;
    subscriptions = [];
    virtualQueueModal: ModalComponent;
    resortReservationDates: Array<string>;

    private _mediaEngineData: any;
    private destroy$ = new Subject<void>();
    private mediaEnginePlayer;

    constructor(
        private configService: ConfigService,
        private elementRef: ElementRef,
        private modalService: ModalService,
        private loadingService: LoadingService,
        public scheduleService: ScheduleService,
        private mealPeriodScheduleService: MealPeriodScheduleService,
        private myPlansService: MyPlansService,
        private logger: LoggerService,
        private viewport: ViewportDetection,
        private localeService: LocaleService,
        private lazyLoader: LazyLoaderService,
        private finderMediaEngine: FinderMediaEngineService,
        @Inject(GET_FULL_DATE_STRING) private getFullDateString: GetFullDateStringProvider,
    ) { }

    async ngOnInit() {
        if (isEmpty(this.aagData)) {
            const state = {
                name: 'Invalid or Empty Data',
                status: STATUS_CODES.INTERNAL_SERVER_ERROR
            };
            this.loadingService.setErrorState(state);
            this.loadingService.setLoadingState(true);
            this.logger.error('Loading SVC -- No AAG Data');
            // We don't want the UI to load if there's no aag data

            return;
        }

        // Set up the modal deep links before any modal is created.
        this.modalDeepLinks = MODAL_DEEP_LINKS;

        if (this.aagData.schedule) {
            // Avoid unnecessary processing
            this.bindSchedules();
            // only show add to my plans if we have schedules
            this.addToMyPlans = this.aagData.addToMyPlans;
            if (this.addToMyPlans) {
                this.createAddToMyPlansModal();
            }
        }

        this.showAag = true;
        this.bindBreadcrumb();
        this.bindActionButton();
        this.animateDetail();
        this.createAccessibilityModal();
        this.createDiscountsModal();
        this.initAvailability();
        this.createVirtualQueueModal();
        this.setBackground();
        this.updateDisclaimerText();
    }

    ngOnDestroy() {
        // Ignore the if just for coverage, there's no need to test unsubscribe
        /* istanbul ignore next */
        this.destroy$.next();
        this.destroy$.complete();
    }

    initMediaEngine() {
        const mediaEngineData = this.mediaEngineData?.data;
        if (!mediaEngineData) {
            return;
        }

        const env = this.configService.getValue(CONFIG_CONSTANTS.envKey);
        const platform = this.viewport.isDesktop() ?
            MEDIA_ENGINE_CONSTANTS.desktop :
            MEDIA_ENGINE_CONSTANTS.mobileWeb;
        const config = this.mediaEngineData?.config ?? {};

        // @TODO: We should try to use this.finderMediaEngine.initConfig
        const finderConfig: FinderMediaEngineConfig = {
            data: mediaEngineData,
            version: this.mediaEngineData.version,
            theme: this.mediaEngineData.theme,
            production: env === CONFIG_CONSTANTS.prodEnvValue,
            fullscreen: {
                label: this.mediaEngineData.fsBtnLabel,
                alt: this.mediaEngineData.fsBtnAlt,
            },
            mediaSelectorAllTabName: this.mediaEngineData.mediaSelectorAllTabName,
            title: this.aagData.title,
            config,
            configOverrides: this.mediaEngineData?.[platform],
            platform,
        };

        const meConfig = this.finderMediaEngine.initMediaEngine(finderConfig);

        this.mediaEngine.version = meConfig.version;
        this.mediaEngine.theme = meConfig.theme;
        this.mediaEngine.production = meConfig.production;
        this.mediaEngine.config = meConfig.config;
    }

    mediaEngineIsReady(mediaEnginePlayer) {
        this.mediaEnginePlayer = mediaEnginePlayer;
    }

    bindBreadcrumb() {
        if (!this.aagData.breadcrumb) {
            return;
        }

        this.breadcrumb = this.aagData.breadcrumb.data;
        this.breadcrumbTemplate = this.aagData.breadcrumb.template;
    }

    bindActionButton() {
        this.actionButtonText = this.aagData.actionButtonText;
        this.actionButtonUrl = this.aagData.actionButtonUrl;
    }

    bindSchedules() {
        this.calendarAction = this.aagData?.schedule?.labels?.calendarAction?.open;
        this.scheduleService.init(this.entityId, this.aagData.schedule, has(this.aagData, 'reserveCta'));
        this.calendarLabels = this.aagData?.schedule?.labels;
    }

    animateDetail() {
        this.detailStateName = 'loaded';
    }

    detailStateDone(event: AnimationEvent) {
        // We want to make sure that the animation is complete and the style has been set
        // by the animation module. Once that is done we can manually remove the style attr.
        setTimeout(() => {
            event['element'].removeAttribute('style');
        });
    }

    /**
     * @name        onDateChanged
     * @description Captures the selected date in the calendar when changed.
     * @param       event {Date} the date selected
     */
    onDateChanged(event) {
        const newDateString = this.getFullDateString(event, this.localeService.getLocale());
        if (newDateString === this.scheduleService.fullFormatDate) {
            return;
        }

        this.scheduleService.getSchedules(event, this.entityId);
        this.mealPeriodScheduleService.getSchedules(event, this.entityId);
        this.resizeMediaEngine();
    }

    onCalendarOpen(event) {
        this.calendarAction = this.aagData.schedule.labels.calendarAction.close;
    }

    onCalendarClose(event) {
        this.calendarAction = this.aagData.schedule.labels.calendarAction.open;
        this.focusDate();
    }

    focusDate() {
        const dateElement = this.getDateElement();
        if (dateElement) {
            dateElement.focus();
        }
    }

    getDateElement() {
        return this.elementRef.nativeElement.querySelector('.date-text .anchor');
    }

    openAccesibilityModal() {
        this.a11yModal.open();
    }

    openDiscountsModal() {
        this.discountsModal.open();
    }

    openVirtualQueueModal() {
        this.virtualQueueModal.open();
    }

    setBackground() {
        if (this.aagData?.background) {
            this.gradientState = false;
            this.setBackgroundImage();
            this.viewport.onViewportResize()
                .pipe(takeUntil(this.destroy$))
                .subscribe(() => this.setBackgroundImage());

            return;
        }

        const gradientUpperLeft = this.aagData?.backgroundGradient?.upperLeft ?? false;
        const gradientLowerRight = this.aagData?.backgroundGradient?.lowerRight ?? false;

        this.gradientEnabledCSS = `linear-gradient(135deg, ${gradientUpperLeft}, ${gradientLowerRight})`;
        this.gradientDisabledCSS = 'none';

        this.updateGradient();
        this.viewport.onViewportResize()
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.updateGradient());
    }

    addToMyPlansAction() {
        if (this.addToMyPlansmModal.is('closed')) {
            this.createAddToMyPlansModal();
        }

        // Check if modal exists
        this.addToMyPlansmModal.ready()
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.openAddToMyPlansModal();
            });
    }

    openAddToMyPlansModal() {
        // openWithLogin will validate if the user is logged in
        // before opening the modal and if its not will redirect to login page.
        this.addToMyPlansmModal.openWithLogin()
            .pipe(takeUntil(this.destroy$))
            .subscribe((swid) => this.myPlansService.swid = swid);
    }

    createVirtualQueueModal() {
        const virtualQueue = this.aagData?.virtualQueue;

        if (!virtualQueue) {
            return;
        }

        this.virtualQueueModal = this.modalService.create(
            VirtualQueueModalComponent,
            {
                title: virtualQueue.modalTitle,
                data: virtualQueue.content,
                hideOnClose: true,
                id: 'virtual-queue-modal'
            }
        );
    }

    createAddToMyPlansModal() {
        this.myPlansService.init({
            calendarLabels: this.calendarLabels,
            entity: {
                id: this.entityId,
                name: this.aagData.title,
                location: this.aagData.location,
            },
            media: this.aagData.media,
            ...this.addToMyPlans
        });
        this.addToMyPlansmModal = this.modalService.create(
            MyPlansModalComponent,
            {
                title: this.addToMyPlans.title,
                hideOnClose: true,
                deepLinkId: this.modalDeepLinks.atmp
            }
        );
        this.atmpModalSubscriptions.push(
            this.afterATMPModalDismiss().subscribe(() => {
                if (this.myPlansService.saved) {
                    this.addToMyPlansmModal.close();
                    // remove all subscriptions for the closed modal
                    forEach(this.atmpModalSubscriptions, (subscription) => subscription.unsubscribe());
                }
            })
        );

        this.addToMyPlansmModal.ready().pipe(
            takeUntil(this.destroy$),
            switchMap(ready => this.addToMyPlansmModal.deepLink())
        ).subscribe(openModal => {
            // if we have the add to my plans deep link active open the modal
            /* istanbul ignore else */
            if (openModal) {
                this.addToMyPlansAction();
            }
        });
    }

    initAvailability() {
        // Grab the default max days
        this.maxDays = this.aagData?.calendar?.maxDays;
        this.resortReservationDates = this.aagData?.calendar?.resortReservationDates;

        // only create the modal if its enabled.
        if (this.aagData?.reserveCta) {
            this.mealPeriodScheduleService.init(this.entityId, this.aagData.mealPeriodsData);
            this.createAvailabilityModal();

            // Set the max calendar days
            this.maxDays = this.aagData?.calendar?.checkAvailabilityMaxDays ?? this.maxDays;
        }
    }

    checkAvailabilityAction() {
        // openWithLogin will validate if the user is logged in
        // before opening the modal and if its not will redirect to login page.
        this.availabilityModal.openWithLogin()
            .pipe(takeUntil(this.destroy$))
            .subscribe();
    }

    createAvailabilityModal() {
        this.availabilityModal = this.modalService.create(
            AvailabilityModalComponent,
            {
                title: this.aagData?.reserveCta?.availabilityModal?.title,
                data: {
                    ...this.aagData?.reserveCta?.availabilityModal,
                    maxDays: this.aagData?.calendar?.checkAvailabilityMaxDays,
                    maxAdditionalDays: this.aagData?.calendar?.maxAdditionalDays,
                    useCalendarAvailApi: this.aagData?.calendar?.getMaxCalDaysFromService ?? false,
                    resortReservationDates: this.aagData?.calendar?.resortReservationDates,
                    entityId: this.entityId.split(';')[0],
                    productName: this.aagData?.title ?? ''
                },
                hideOnClose: true,
                deepLinkId: this.modalDeepLinks.checkAvailability,
                theme: 'dynamic'
            }
        );
        // if we have the deep link active open the modal
        this.availabilityModal.ready()
            .pipe(
                takeUntil(this.destroy$),
                switchMap(ready => this.availabilityModal.deepLink())
            )
            .subscribe(openModal => {
                /* istanbul ignore else */
                if (openModal) {
                    this.checkAvailabilityAction();
                }
            });
    }

    updateGradient() {
        const isDesktop = this.viewport.isDesktop();
        this.gradientState = isDesktop ? this.gradientEnabledCSS : this.gradientDisabledCSS;

        if (this.aagData?.type === 'guest-service') {
            this.gradientState = 'none';
        }
    }

    updateDisclaimerText() {
        if (!this.aagData.disclaimer) {
            return;
        }
        const { phoneNumber, template } = this.aagData.disclaimer;
        if (phoneNumber && template) {
            this.disclaimerText = template.replace('{phone}', phoneNumber);
        }
    }

    private afterATMPModalDismiss() {
        return this.addToMyPlansmModal.ready()
            .pipe(
                takeUntil(this.destroy$),
                switchMap(() => this.addToMyPlansmModal.afterDismiss())
            );
    }

    private createAccessibilityModal() {
        if (!this.aagData.accessibilityModal) {
            return;
        }
        this.a11yModal = this.modalService.create(
            AccessibilityModalComponent,
            {
                title: this.aagData.accessibilityModal.title,
                hideOnClose: true,
                data: this.aagData.accessibilityModal.data,
                id: 'a11y-modal'
            }
        );
    }

    private createDiscountsModal() {
        const sections = this.aagData?.discountsModal?.sections;
        this.discountsModalLabels = this.aagData?.discountsModal?.labels;
        this.showDiscountsModal = (sections && Object.keys(sections).length > 0);

        if (!this.showDiscountsModal) {
            return;
        }

        this.lazyLoader.lazyLoadModule('discounts-modal', null, {}).then(finderDiscountsModalComponent => {
            this.discountsModal = this.modalService.create(
                finderDiscountsModalComponent,
                {
                    title: `${this.discountsModalLabels.title} ${this.aagData.title}`,
                    hideOnClose: true,
                    deepLinkId: this.modalDeepLinks.discounts,
                    data: this.aagData.discountsModal,
                    id: 'discount-modal'
                }
            );

            this.discountsModal.ready()
                .pipe(
                    takeUntil(this.destroy$),
                    switchMap(ready => this.discountsModal.deepLink())
                )
                .subscribe(openModal => {
                    /* istanbul ignore else */
                    if (openModal) {
                        this.discountsModal.open();
                    }
                });
        });
    }

    private resizeMediaEngine() {
        /* istanbul ignore else */
        if (this.mediaEnginePlayer) {
            this.mediaEnginePlayer.nicelyFitMediaEngine();
        }
    }

    private setBackgroundImage() {
        const el = this.elementRef.nativeElement;
        let url = this.aagData?.background?.desktop?.bgImage?.url;
        let position = this.aagData?.background?.desktop?.bgPosition;
        let repeat = this.aagData?.background?.desktop?.bgRepeat;
        if (this.viewport.isMobile()) {
            url = this.aagData?.background?.mobile?.bgImage?.url;
            position = this.aagData?.background?.mobile?.bgPosition;
            repeat = this.aagData?.background?.mobile?.bgRepeat;
        }

        el.style.setProperty('--finder-aag-details-background-image', `url(${url})`);
        el.style.setProperty('--finder-aag-details-background-position', position);
        el.style.setProperty('--finder-aag-details-background-repeat', repeat);
    }
}
